import i18next from 'i18next';
import { featureFlags, pages } from '@dvag/dfs-constant-config';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';

import { AnalyseBasicSectionListData } from 'type/sectionList';
import { IconName } from '@dvag/design-system-icons/dist/types/types/icon-types';

export interface LeftCardSectionData extends Omit<AnalyseBasicSectionListData, 'path' | 'params'> {
  navigate: (householdId: string) => void;
  isGrouped?: boolean;
  subSections?: LeftCardSectionData[];
  contentIcon?: IconName;
  policy?: string;
}

const LeftCardEnums = {
  HOUSEHOLD_BUDGET: 'haushaltsbudget-ausgaben',
  CONTRACT_CHECK: 'haushaltsbudget-vertragsuebersicht',
  PENSION: 'basisdaten-staatliche-rente',
  LIVING_SITUATION: 'basisdaten-wohnsituation',
  BALANCE_SHEET: 'vermoegensbilanz',
  COVERAGE_GAPS: 'versorgungsluecken',
  COVERAGE_GAPS_WORKFORCE: 'versorgungsluecke-arbeitskraft',
  COVERAGE_GAPS_PENSION: 'versorgungsluecke-rente',
};

export const {
  HOUSEHOLD_BUDGET,
  CONTRACT_CHECK,
  PENSION,
  LIVING_SITUATION,
  BALANCE_SHEET,
  COVERAGE_GAPS,
  COVERAGE_GAPS_WORKFORCE,
  COVERAGE_GAPS_PENSION,
} = LeftCardEnums;

export const COVERAGE_GAPS_FEATURE_FLAG = featureFlags.canAccessCoverageGapsConsultation;

export const COVERAGE_GAPS_POLICIES = {
  overview: 'app-web-vp-digital.allfinanzinhalte-versorgungsluecken-beratungsdashboard.zugriff',
  workforce: 'app-web-vp-digital.versorgungsluecke-arbeitskraft-beratung.zugriff',
  pension: 'app-web-vp-digital.versorgungsluecke-rente-beratung.zugriff',
} as const;

const coverageGapsUrl = (householdId: string, tab: string) =>
  `/${appNames.coverageGapsConsultation}/haushalt/${householdId}/${tab}`;

const navigateToCoverageGapsTab = (householdId: string, tab: string) => {
  navigate(coverageGapsUrl(householdId, tab), `/berater${globalThis.location.pathname}`);
};

export const leftCardSections: LeftCardSectionData[] = [
  {
    id: HOUSEHOLD_BUDGET,
    title: i18next.t('dashboardCards.financialContent.householdBudget'),
    icon: 'immobilienfinanzierung',
    navigate: (householdId: string) => {
      navigate(pages.FFG_HOUSEHOLD_BUDGET.getUrl(householdId, true));
    },
  },
  {
    id: CONTRACT_CHECK,
    title: i18next.t('dashboardCards.financialContent.contractReview'),
    icon: 'klemmbrett',
    navigate: (householdId: string) => {
      navigate(pages.FFG_CONTRACT_OVERVIEW.getUrl(householdId, true));
    },
  },
  {
    id: PENSION,
    title: i18next.t('dashboardCards.financialContent.pensionInformation'),
    icon: 'sessel',
    navigate: (householdId: string) => {
      navigate(
        pages.PENSION.getUrl(householdId, true),
        pages.LIVING_SITUATION.getUrl(householdId, true),
      );
    },
  },
  {
    id: LIVING_SITUATION,
    title: i18next.t('dashboardCards.financialContent.livingSituation'),
    icon: 'haus',
    navigate: (householdId: string) => {
      navigate(
        pages.LIVING_SITUATION.getUrl(householdId, true),
        `/berater${window.location.pathname}`,
      );
    },
  },
  {
    id: COVERAGE_GAPS,
    title: i18next.t('dashboardCards.financialContent.coverageGaps.title'),
    icon: 'versicherung-und-vorsorge',
    contentIcon: 'chevron-rechts',
    policy: COVERAGE_GAPS_POLICIES.overview,
    navigate: () => undefined,
    subSections: [
      {
        id: COVERAGE_GAPS_WORKFORCE,
        title: i18next.t('dashboardCards.financialContent.coverageGaps.workforce'),
        icon: 'arbeitskraftabsicherung',
        policy: COVERAGE_GAPS_POLICIES.workforce,
        navigate: (householdId: string) => {
          navigateToCoverageGapsTab(householdId, 'arbeitskraft');
        },
      },
      {
        id: COVERAGE_GAPS_PENSION,
        title: i18next.t('dashboardCards.financialContent.coverageGaps.pension'),
        icon: 'sessel',
        policy: COVERAGE_GAPS_POLICIES.pension,
        navigate: (householdId: string) => {
          navigateToCoverageGapsTab(householdId, 'rente');
        },
      },
    ],
  },
  {
    id: BALANCE_SHEET,
    title: i18next.t('dashboardCards.financialContent.balanceSheet'),
    icon: 'rechtsschutz-02',
    navigate: (householdId: string) => {
      navigate(pages.FINANCIAL_BALANCE_SHEET.getUrl(householdId, true), undefined, true);
    },
  },
];
